import {
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { MeetingItem, MeetingItemType, MeetingStatus, UserRole } from "../../models";
import * as userActions from "../../actions/userActions";
import * as itemActions from "../../actions/itemActions";
import * as listActions from "../../actions/listActions";
import * as projectActions from "../../actions/projectActions";
import { Context } from "../../components/context";
import { AppContext } from "../../models/applicationState";
import { ItemActions } from "../../actions/itemActions";
import { ListActions } from "../../actions/listActions";
import { useNavigate, useParams } from "react-router-dom";
import { bindActionCreators } from "../../actions/actionCreators";
import { withApplicationInsights } from "../../components/telemetry";
import { MeetingHeader } from "../../components/meetingHeader";
import { SelectProject } from '../../components/selectProject';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import CrudGrid from "../../components/crudGrid";
import CustomToolbar from "../../components/customToolbar";
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowId, GridSortDirection, GridSortModel, GridTreeNodeWithRender } from "@mui/x-data-grid";
import { MeetingItemRepresentation, actorColumn, categoryColumn, contentColumn, convertToRepresentationSortedByCreateDateAscending, decisionColumn, dueDateColumn, emptyItemRepresentation, externalEmailMarkerColumn, getMeetingItemFromRepresentation, hasRepresentationChanged, indexColumn, informationColumn, isoStringToDueDate, isStatusUpdated, tagsColumn, taskColumn } from "../../models/meetingItemRepresentation";
import EditableDueDateCell from "../../components/editableDueDateCell";
import { Checkbox, TextField, Tooltip } from "@mui/material";
import { AttachmentsModal } from "../../components/attachmentsModal";
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskIcon from '@mui/icons-material/Task';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import SignpostIcon from '@mui/icons-material/Signpost';
import SignpostOutlinedIcon from '@mui/icons-material/SignpostOutlined';
import { FreeSingleSelect } from "../../components/freeSingleSelect";
import { useTranslation } from "react-i18next";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TooltipIcon } from "../../components/tooltipIcon";
import ExternalEmailMarker from "../../components/externalEmailMarker";
import { TooltipIconWithBadge } from "../../components/tooltipIconWithBadge";
import { FreeCheckboxMultipleSelect } from "../../components/freeCheckboxMultipleSelect";
import { Tag } from "../../models/tag";
import { Project } from "../../models/project";
import {
    ItemEditingPanel, addStatusUpdateSystemCommentToItem, hasUserComments, addContentChangeCommentToItem,
    addDueDateChangeCommentToItem, addPrioritizationChangeCommentToItem, addRiskChangeCommentToItem, addActorChangeCommentToItem
} from "../../components/itemEditingPanel";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useMsal } from "@azure/msal-react";
import { red } from '@mui/material/colors';
import ConfirmationPanel from "../../components/confirmationPanel";

const ItemsPage = () => {
    const ITEM = "item";
    const ITEM_SORT_FIELD = "item.sortField";
    const ITEM_SORT_DIRECTION = "item.sortDirection";
    const navigate = useNavigate();
    const { t } = useTranslation();
    const appContext = useContext<AppContext>(Context);
    const { meetingId } = useParams();
    const actions = useMemo(
        () => ({
            lists: bindActionCreators(
                listActions,
                appContext.dispatch
            ) as unknown as ListActions,
            items: bindActionCreators(
                itemActions,
                appContext.dispatch
            ) as unknown as ItemActions,
        }),
        [appContext.dispatch]
    );

    const [showTasksWarning, setShowTasksWarning] = useState(false);
    const [showAttachmentsPane, setShowAttachmentsPane] = useState(false);
    const [items, setItems] = useState<MeetingItemRepresentation[]>([]);
    const [showAttachmentsForItem, setShowAttachmentsForItem] = useState(undefined);
    const [showEditingPanelForItem, setShowEditingPanelForItem] = useState(undefined);
    const [selectedProject, setSelectedProject] = useState<Project | undefined>();
    const [projects, setProjects] = useState<Project[]>(appContext.state.invoker?.projects || []);

    const [meetingName, setMeetingName] = useState(appContext.state.selectedList?.name);
    const [meetingDate, setMeetingDate] = useState(appContext.state.selectedList?.date);
    const { instance } = useMsal();
    const reviewableItems = appContext.state.selectedList?.items || [];
    const areMeetingItemsLoaded = Array.isArray(appContext.state.selectedList?.items);
    // React to selected list changes
    useEffect(() => {
        if (meetingId && appContext.state.selectedList?.id !== meetingId) {
            actions.lists.load(meetingId);
        }
    }, [actions.lists, appContext.state.selectedList, meetingId, navigate])

    useEffect(() => {
        const invokerRole = appContext.state.invoker?.role;
        if (!invokerRole) {
            return;
        }

        if (invokerRole !== UserRole.Admin) {
            return;
        }

        if (appContext.state.invoker?.projects?.length) {
            return;
        }

        const loadProjects = async () => {
            const projectsList = await projectActions.list();
            setProjects(projectsList);
        };

        loadProjects();
    }, [appContext.state.invoker?.role, appContext.state.invoker?.projects]);

    useEffect(() => {
        if (appContext.state.invoker?.projects?.length) {
            setProjects(appContext.state.invoker?.projects);
        }
    }, [appContext.state.invoker?.projects]);

    useEffect(() => {
        const project = (projects?.length && appContext.state.selectedList?.project) ? projects.find(p => p.id === appContext.state.selectedList?.project) : undefined;
        setMeetingName(appContext.state.selectedList?.name + (project ? ` (${project.name})` : ''));
        setMeetingDate(appContext.state.selectedList?.date);
        setSelectedProject(project);
    }, [projects, appContext.state.selectedList?.project]);

    const filterOnlyLiveItems = (items: MeetingItem[]) => {
        return items?.filter(i => !i.isSnapshot) || [];
    }

    // Load items for selected list
    useEffect(() => {
        if (appContext.state.selectedList?.id && !appContext.state.selectedList.items) {
            const loadListItems = async (listId: string) => {
                await actions.items.list(listId);
            }

            loadListItems(appContext.state.selectedList.id)
        }
        if (appContext.state.selectedList?.items) {
            setItems(convertToRepresentationSortedByCreateDateAscending(filterOnlyLiveItems(appContext.state.selectedList.items)));
        }
    }, [actions.items, appContext.state.selectedList?.id, appContext.state.selectedList?.items])

    const onItemUpserted = async (newItem: MeetingItemRepresentation, oldItem?: MeetingItemRepresentation) => {
        // This check avoids item creation from the top row of the crud grid on edit
        if (newItem.id === emptyItemRepresentation.id) {
            return;
        }
        newItem.project = appContext.state.selectedList.project;
        newItem.tags = newItem.tags || appContext.state.selectedList.defaultTags;
        // reset newItem content to the last saved content if it is empty
        newItem.content = newItem.content || newItem.data.content;
        const statusUpdated = isStatusUpdated(newItem);
        if (statusUpdated) {
            const userFullName = (appContext.state.invoker.lastName && appContext.state.invoker.firstName) ? (appContext.state.invoker?.firstName + ' ' + appContext.state.invoker?.lastName) : instance.getActiveAccount()?.name;
            addStatusUpdateSystemCommentToItem(newItem, userFullName, t)
        }
        const item = getMeetingItemFromRepresentation(newItem);
        if (!item.types.includes(MeetingItemType.Task)) {
            item.actor = '';
        }
        const meeting = appContext.state.selectedList;
        // if (item.actor) {
        //     if (!meeting.participants?.includes(item.actor) && !meeting.viewers?.includes(item.actor)) {
        //         // We have assigned a task to an email not in participants. Add that email as viewer on the meeting.
        //         if (!meeting.viewers) {
        //             meeting.viewers = [item.actor];
        //         } else {
        //             meeting.viewers.push(item.actor);
        //         }
        //         await actions.lists.save(meeting);
        //         await actions.lists.list();
        //     }
        // }

        if (hasRepresentationChanged(newItem)) {
            item.dueDate = item.dueDate ?? null;
            const savedItem = await actions.items.save(meeting.id, item);
            // Upsert category/email suggestions (emails only if valid) after successful save
            const upsert = (arr: Array<{ value: string; count: number }>, val?: string) => {
                if (!val) return arr;
                const v = val.toString().trim();
                if (!v) return arr;
                const idx = arr.findIndex(e => e.value === v);
                if (idx >= 0) {
                    const next = [...arr];
                    next[idx] = { value: v, count: (next[idx]?.count ?? 0) + 1 };
                    return next;
                }
                return [...arr, { value: v, count: 1 }];
            };
            const invoker = appContext.state.invoker;
            if (invoker) {
                const nextInvoker = { ...invoker } as typeof invoker & { categorySuggestions?: { value: string; count: number }[]; emailSuggestions?: { value: string; count: number }[] };
                let changed = false;
                if (item.category) {
                    const currentCats = (invoker.categorySuggestions || []) as Array<{ value: string; count: number }>;
                    nextInvoker.categorySuggestions = upsert(currentCats, item.category);
                    changed = true;
                }
                if (item.actor && isValidEmail(item.actor)) {
                    const currentEmails = (invoker.emailSuggestions || []) as Array<{ value: string; count: number }>;
                    nextInvoker.emailSuggestions = upsert(currentEmails, item.actor);
                    changed = true;
                }
                if (changed) {
                    appContext.dispatch(userActions.setInvokerAction(nextInvoker));
                    userActions.save(nextInvoker)
                        .then(() => userActions.getInvoker()(appContext.dispatch))
                        .catch(() => {/* non-blocking */ });
                }
            }

            // Change meeting status to Draft if it was Planned
            if (meeting.status === MeetingStatus.Planned) {
                meeting.status = MeetingStatus.Draft;
                await actions.lists.save(meeting);
                await actions.lists.list(); // Refresh meeting list
            }

            return savedItem;
        }
    }

    const setMeetingInReview = () => {
        const meeting = appContext.state.selectedList;
        if (meeting.status !== MeetingStatus.InReview && meeting.status !== MeetingStatus.Published) {
            meeting.status = MeetingStatus.InReview;
            actions.lists.save(meeting);
        }
    }

    const onRowChanged = async (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>, row: MeetingItemRepresentation) => {
        if (!row.task
            && "actor" === params.field
            && row.actor !== undefined)
            row.task = true;
        else if (params.field !== "informationType" && !row.decision && !row.task)
            row.info = true;
        if (!row.task) {
            params.row.dueDate = null;
        }

        onItemUpserted(params.row);
        params.api.updateRows([{ ...params.row }]);
    }

    const onItemDeleted = (itemRepresentation: MeetingItemRepresentation) => {
        const item = getMeetingItemFromRepresentation(itemRepresentation);
        if (item.id) {
            actions.items.remove(appContext.state.selectedList.id, item);
        }
    }

    const onItemUpdated = async (itemRepresentation: MeetingItemRepresentation) => {
        const meeting = appContext.state.selectedList;
        const userFullName = (appContext.state.invoker.lastName && appContext.state.invoker.firstName)
            ? (appContext.state.invoker?.firstName + ' ' + appContext.state.invoker?.lastName)
            : instance.getActiveAccount()?.name;

        // Find previous item for comparison
        const previousItem = items.find(row => row.id === itemRepresentation.id);

        // Add comments for all relevant changes (status, due date, risk, content, prioritization)
        if (previousItem && previousItem.status !== itemRepresentation.status) {
            addStatusUpdateSystemCommentToItem(itemRepresentation, userFullName, t);
        }
        if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
            addDueDateChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.dueDate, t);
        }
        if (previousItem && previousItem.risk !== itemRepresentation.risk) {
            addRiskChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.risk, t);
        }
        if (previousItem && previousItem.content !== itemRepresentation.content) {
            addContentChangeCommentToItem(itemRepresentation, userFullName, t);
        }
        if (previousItem && previousItem.prioritized !== itemRepresentation.prioritized) {
            addPrioritizationChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.prioritized, t);
        }
        if (previousItem && previousItem.actor !== itemRepresentation.actor) {
            addActorChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.actor || '', t);
        }

        const item = getMeetingItemFromRepresentation(itemRepresentation);
        await actions.items.save(meeting.id, item);
    }

    const handleProjectSelect = async (project: Project) => {
        setSelectedProject(project);

        const meeting = appContext.state.selectedList;

        meeting.project = project.id;

        for (const itemRepresentation of items) {
            const item = getMeetingItemFromRepresentation(itemRepresentation);
            item.project = project.id; // Set the new project ID
            await actions.items.save(meeting.id, item);
        }

        await actions.lists.save(meeting);
    };

    const checkTasksForReview = (): MeetingItem[] => {
        let reviewTasks = reviewableItems.filter(item => item.types.includes("task"));
        reviewTasks = reviewTasks.filter(t => !t.dueDate || !t.actor);
        return reviewTasks;
    }

    const prepareForReview = () => {
        if (!areMeetingItemsLoaded || !appContext.state.selectedList?.id) {
            return;
        }

        if (appContext.state.selectedList?.status !== MeetingStatus.Published && checkTasksForReview().length > 0) {
            setShowTasksWarning(true);
        } else {
            setMeetingInReview();
            navigate(`/meetings/${appContext.state.selectedList?.id}/review`);
        }
    }

    const showReviewMessage = () => {
        const noOfTasks = checkTasksForReview().length;
        return t('review_warning.task', { count: noOfTasks });
    }

    const openItemAttachments = (id: GridRowId, meetingId: string) => () => {
        setShowAttachmentsForItem({ id, meetingId });
    };

    const openItemEditingPanel = (item: MeetingItemRepresentation) => () => {
        setShowEditingPanelForItem({ item });
    };

    const isValidEmail = (email: string) => {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        return emailPattern.test(email);
    }

    const isEmptyOrValidEmail = (email: string) => {
        if (email === undefined || email === "") {
            return true;
        }

        if (typeof email === 'string' && email.trim() === "") {
            return false;
        }

        return isValidEmail(email);
    }


    const itemNavigateActions = (id: GridRowId, row: MeetingItemRepresentation) => {
        return ([
            <GridActionsCellItem
                key="open"
                icon={<TooltipIconWithBadge titleKey={'general.open'} icon={<ChevronRightIcon />} badgeContent={hasUserComments(row, t) ? "i" : undefined} />}
                label="Edit"
                onClick={openItemEditingPanel(row)}
                disabled={!row.data}
                style={{ color: "var(--mui-palette-primary-main)" }}
            />,
            <GridActionsCellItem
                key="attachments"
                icon={
                    <TooltipIconWithBadge
                        badgeContent={row.attachments}
                        titleKey='dashboard.itemAttachments'
                        icon={<AttachFileIcon />}
                    />
                }
                label={t('dashboard.itemAttachments_label')}
                onClick={openItemAttachments(id, row.meetingId)}
                style={{ color: "var(--mui-palette-primary-main)" }}
            />]);
    }

    const formatInputs = (values: (Tag | string)[]): string[] => {
        return values.map(v => {
            if (typeof v === 'string') {
                return v;
            }
            return v.id;
        });
    };

    const getTagOptions = (project: Project) => {
        return project?.tags || [];
    }

    const getFreeMultipleSelectComponentForTags = (saveUpsert: boolean) => {
        return {
            ...tagsColumn,
            editable: false,
            renderCell: (params) => (
                <FreeCheckboxMultipleSelect
                    onValueSelected={(value) => {
                        params.row.tags = value;
                        if (saveUpsert) {
                            onItemUpserted(params.row);
                        }
                        params.api.updateRows([{ id: params.row.id, data: params.row }]);
                    }}
                    options={getTagOptions(projects.find(p => p.id === appContext.state.selectedList?.project))}
                    values={(params.row.tags || appContext.state.selectedList?.defaultTags || []).filter(t => projects.find(p => p.id === appContext.state.selectedList.project)?.tags?.map(t => t.id).includes(t))}
                    placeholder={saveUpsert ? t('item.no_tags_selected') : t('item.add_default_tags')}
                    formatValues={(values) => {
                        const valueIds = values.map(v => v.id ? v.id : v);

                        return projects.find(p => p.id === appContext.state.selectedList?.project)?.tags.filter(
                            t => valueIds.includes(t.id)
                        ).map(t => t.name)
                    }}
                    formatInputs={formatInputs}
                    isOptionEqualToValue={(option, value) => option.id === value}
                    disableFreeSolo={true}
                />
            )
        };
    }

    // Helper to derive a domain string (including leading @) from an email
    const getDomainFromEmail = (email?: string): string | undefined => {
        if (email && email.includes('@')) {
            const domain = email.split('@')[1]?.toLowerCase();
            if (domain) {
                // Check if it's a valid domain (contains at least one dot and follows domain naming conventions)
                const isValidDomain = domain.includes('.') &&
                    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(domain);

                if (isValidDomain) {
                    const isExternal = domain !== 'example.com'; // Example of checking for a specific domain
                    // Add label for external domains
                    return isExternal ? `@${domain} (external)` : `@${domain}`;
                } else {
                    // If it's not a valid domain (just a word/name), return without external label
                    return `@${domain}`;
                }
            }
        }
        return undefined;
    };

    const columns: GridColDef[] = [
        {
            ...indexColumn,
            valueFormatter: (_value, row) => row.index?.toString().padStart(2, '0')
        },
        {
            ...categoryColumn,
            width: 130,
            editable: true,
            renderEditCell: (params) => {
                return (
                    <FreeSingleSelect
                        disableFreeSolo={false}
                        onValueSelected={(value) => {
                            params.api.setEditCellValue({ id: params.id, field: 'category', value });
                            params.api.stopCellEditMode({ id: params.id, field: 'category' });
                        }}
                        options={[]}
                        sx={{ marginTop: "15px", marginLeft: "15px" }}
                        value={params.value || ''}
                        placeholder={t('item.category_label')}
                        hideTooltip={true}
                        useCategorySuggestions={true}
                        enableUsageSort={true}
                        enableDelete={true}
                        maxVisibleItems={6}
                    />
                );
            }
        },
        {
            ...contentColumn, editable: true, flex: 0.6, renderEditCell: (params) => {
                return (
                    <TextField
                        label={t("item.content_label")}
                        sx={{ marginTop: "10px", border: 0 }}
                        value={params.value || ''}
                        placeholder={t("item.content_label")}
                        onChange={(event) => {
                            params.api.setEditCellValue({ id: params.id, field: 'content', value: event.target.value });
                        }}
                        onBlur={params.stopCellEditMode}
                        variant="outlined"
                        fullWidth
                        onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                                e.stopPropagation();
                            }
                        }} slotProps={{ input: {
                            sx: {
                                border: 0, // Remove border from input element
                                '& fieldset': {
                                    border: 'none', // Additionally remove fieldset border for outlined variant
                                },
                            }
                        } }}
                    />
                )
            }
        },
        {
            ...taskColumn as GridColDef,
            renderCell: (params) => (
                <Tooltip title={t('item.taskType')}>
                    <Checkbox
                        icon={<TaskOutlinedIcon />}
                        checkedIcon={<TooltipIcon titleKey={'general.open'} icon={<TaskIcon />} />}
                        sx={{ color: 'secondary.light' }}
                        checked={params.row.task}
                        tabIndex={-1}
                        onChange={() => {
                            params.row.task = !params.row.task;
                            if (!params.row.task) {
                                params.row.actor = undefined;
                            }
                            onRowChanged(params, params.row);
                        }}
                    />
                </Tooltip>
            )
        },
        {
            ...decisionColumn as GridColDef,
            renderCell: (params) => (
                <Tooltip title={t('item.decisionType')}>
                    <Checkbox
                        icon={<SignpostOutlinedIcon />}
                        checkedIcon={<SignpostIcon />}
                        sx={{ color: 'secondary.light' }}
                        tabIndex={-1}
                        checked={params.row.decision}
                        onChange={() => {
                            params.row.decision = !params.row.decision;
                            onRowChanged(params, params.row);
                        }}
                    />
                </Tooltip>
            )
        },
        {
            ...informationColumn as GridColDef,
            renderCell: (params) => (
                <Tooltip title={t('item.informationType')}>
                    <Checkbox
                        icon={<InfoOutlinedIcon />}
                        checkedIcon={<TooltipIcon titleKey={'general.open'} icon={<InfoIcon />} />}
                        sx={{ color: 'secondary.light' }}
                        tabIndex={-1}
                        checked={params.row.info}
                        onChange={() => {
                            params.row.info = !params.row.info;
                            onRowChanged(params, params.row);
                        }}
                    />
                </Tooltip>
            )
        },
        {
            ...actorColumn as GridColDef,
            width: 280,
            renderCell: (params) => (
                <FreeSingleSelect
                    disableFreeSolo={false}
                    onValueSelected={(value) => {
                        params.row.actor = value;
                        onRowChanged(params, params.row);
                    }}
                    options={(() => {
                        const participants = (appContext.state.selectedList?.participants || []).filter(Boolean) as string[];
                        const participantSet = new Set(participants);
                        const emailSuggestions = (appContext.state.invoker?.emailSuggestions || []) as Array<{ value: string; count: number }>;
                        const usageSortedOthers = emailSuggestions
                            .filter(e => !!e?.value && !participantSet.has(e.value))
                            .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.value.localeCompare(b.value))
                            .map(e => e.value);
                        const ordered: string[] = [];
                        const pushUnique = (val?: string) => {
                            if (!val) return;
                            if (!ordered.includes(val)) ordered.push(val);
                        };
                        participants.forEach(pushUnique);
                        usageSortedOthers.forEach(pushUnique);
                        // Ensure the current cell value stays selectable even if not in lists
                        pushUnique(params.row.actor as string);
                        return ordered;
                    })()}
                    value={params.row.actor}
                    placeholder={actorPlaceholder(params)}
                    disabled={isEditableCell(params)}
                    sx={{ backgroundColor: isEmptyOrValidEmail(params.row.actor) ? 'transparent' : red[50], paddingLeft: '5px' }}
                    hideTooltip={isEmptyOrValidEmail(params.row.actor)}
                    tooltipTitle={t('item.invalidEmail')}
                    useEmailSuggestions={true}
                    enableUsageSort={false}
                    preserveOptionOrder={true}
                    dropdownMinWidth={340}
                />
            )
        },
        {
            // Yellow "E" marker after actor email when it's external
            ...externalEmailMarkerColumn as GridColDef,
            renderCell: (params) => {
                const actor: string | undefined = params.row.actor;
                return (<ExternalEmailMarker email={actor} internalFromEmail={appContext.state.selectedList?.creator} />);
            }
        },
        {
            ...dueDateColumn as GridColDef,
            editable: false,
            minWidth: 145,
            maxWidth: 145,
            renderCell: (params) => {
                const { task, decision, info } = params.row;
                // Mirror legacy isEditableCell: editable for tasks and for
                // blank rows (setting a date converts them to tasks below).
                const canEdit = task || (!decision && !info);
                if (!canEdit) {
                    return (
                        <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                            {t('item.notAvailable')}
                        </span>
                    );
                }
                return (
                    <EditableDueDateCell
                        taskId={String(params.row.id)}
                        dueDate={params.row.dueDate}
                        onChange={(_taskId, newISODate) => {
                            const newRow: MeetingItemRepresentation = {
                                ...params.row,
                                dueDate: isoStringToDueDate(newISODate) as Date,
                            };
                            if (!newRow.task) newRow.task = true;
                            onItemUpserted(newRow);
                            params.api.updateRows([{ ...newRow }]);
                        }}
                    />
                );
            },
        }
    ];

    const topColumns: GridColDef[] = [...columns];
    if (projects?.length) {
        topColumns.push(getFreeMultipleSelectComponentForTags(false));
    }
    const bottomColumns: GridColDef[] = [...columns];
    if (projects?.length) {
        bottomColumns.push(getFreeMultipleSelectComponentForTags(true));
    }

    async function updateSortModel(newSortModel: GridSortModel) {
        localStorage.setItem(ITEM_SORT_FIELD, newSortModel[0]?.field);
        localStorage.setItem(ITEM_SORT_DIRECTION, newSortModel[0]?.sort);
    }

    const isEditableCell = (params: any) => {
        const { task, decision, info } = params.row;
        const isActor = params.field === 'actor';
        const isDueDate = params.field === 'dueDate';

        if (isActor)
            return !task && (decision || info);
        else if (isDueDate)
            return task || (!task && !decision && !info && isDueDate);
        else
            return true;
    };

    const actorPlaceholder = (params: any) => {
        if (params.row.task) {
            return t('item.task_label');
        }

        return !isEditableCell(params)
            ? t('item.taskType').toLowerCase()
            : t('item.notAvailable');
    };

    return (
        <Box>
            <CssBaseline />
            <MeetingHeader meetingName={meetingName}
                meetingDate={meetingDate}
                primaryBtnText={t('button.attachments', { count: appContext.state.selectedList?.attachments || 0 })}
                onPrimaryBtnClick={() => setShowAttachmentsPane(true)}
                secondaryBtnText={t('button.review')}
                disableSecondaryBtn={!areMeetingItemsLoaded}
                onSecondaryBtnClick={() => prepareForReview()}
                actionButtons={[
                    <SelectProject
                        key="project-selector"
                        projects={projects}
                        selectedProject={selectedProject}
                        onProjectSelect={handleProjectSelect}
                        variant="outlined"
                        sx={{ marginRight: 1 }}
                    />
                ]}
                backRouteParent="/meetings"
            />
            {showTasksWarning && (
                <ConfirmationPanel
                    showModal={showTasksWarning}
                    hideModal={() => setShowTasksWarning(false)}
                    confirmModal={() => {
                        setMeetingInReview();
                        navigate(`/meetings/${appContext.state.selectedList?.id}/review`);
                    }}
                    title={t('text.warning.warning_confirmation')}
                    content={showReviewMessage()}
                />
            )}

            {showAttachmentsPane && (
                <AttachmentsModal
                    open={true}
                    allowModifications={true}
                    height='70vh'
                    uploadPrefix={`meetings/${appContext.state.selectedList?.id}/`}
                    meetingId={appContext.state.selectedList?.id}
                    onClose={() => setShowAttachmentsPane(false)}
                    onAttachmentsCountChanged={(count, _meetingId, _itemId) => { appContext.state.selectedList.attachments = count }}
                />
            )}

            {showAttachmentsForItem
                && (
                    <AttachmentsModal
                        open={true}
                        allowModifications={true}
                        height='70vh'
                        uploadPrefix={showAttachmentsForItem.meetingId ? `items/meetings/${showAttachmentsForItem.meetingId}/items/${showAttachmentsForItem.id}/` : `items/quicktasks/${showAttachmentsForItem.id}/`}
                        meetingId={showAttachmentsForItem.meetingId}
                        itemId={showAttachmentsForItem.id}
                        onClose={() => setShowAttachmentsForItem(undefined)}
                        onAttachmentsCountChanged={(count, _meetingId, itemId) => setItems(items.map(item => item.id === itemId ? { ...item, attachments: count } : item))}
                    />
                )}

            {showEditingPanelForItem
                && (
                    <ItemEditingPanel
                        allowMetadataEditing
                        allowAttachmentsEditing={true}
                        meetingItem={showEditingPanelForItem.item}
                        meeting={appContext.state.selectedList}
                        open={showEditingPanelForItem}
                        onClose={() => setShowEditingPanelForItem(undefined)}
                        onUpdate={onItemUpdated}
                        onAttachmentsCountChanged={(count, _meetingId, itemId) => setItems(items.map(item => item.id === itemId ? { ...item, attachments: count } : item))}
                    />
                )}

            <Container maxWidth={false} disableGutters>
                <CrudGrid
                    processRowUpdate={onItemUpserted}
                    onDelete={onItemDeleted}
                    rows={items}
                    columns={bottomColumns}
                    height='80vh'
                    emptyRow={emptyItemRepresentation}
                    modelType={ITEM}
                    topColumns={topColumns}
                    mandatoryColumnNames={[contentColumn.field]}
                    extraActions={itemNavigateActions}
                    actionsHeaderKey=''
                    isCellEditable={isEditableCell}
                    sortField={localStorage.getItem(ITEM_SORT_FIELD)}
                    newItemsToBottom={true}
                    sortDirection={localStorage.getItem(ITEM_SORT_DIRECTION) as GridSortDirection}
                    updateSortModel={updateSortModel}
                    toolbar={CustomToolbar}
                    toolbarProps={{
                        showSettings: false,
                        hideItemStatusToggles: true
                    }}
                />
            </Container>
        </Box>
    );
};

export default withApplicationInsights(ItemsPage, "ItemsPage");
